// useSupplierFeature — which phase of the supplier feature is switched on
// (specs/20260930-leveranciers-tickets-interventies, plan PD-1 / PD-2, task T112).
//
// The API carries ONE flag, SUPPLIERS_ENABLED (false | 1..5 | true). With the flag below a
// route's phase every supplier route answers 404 — including for admin/super_admin, who pass
// every permission check. So permission alone cannot decide whether a supplier nav item or
// route is shown: it is `isItemVisible(item) && phase >= 1`, never a `module` field (P-12).
//
// FAIL CLOSED. Loading, an error, a 404 from an API that predates the route, or a body without
// a numeric phase all read as phase 0 — nothing supplier-related is shown.
//
// One request per page load, shared by every consumer (Layout, the route gate, the ticket
// sidebar, the ticket list). A successful answer is kept for the session; a failure is kept
// for a minute so a transient error does not hide the feature until the next full reload.
//
// `isSupplierMember` (assumption, see the T112 report): the same endpoint tells whether the
// caller is a member of at least one INTERNAL supplier of their tenant, so the ticket list
// can offer "Mijn toegewezen tickets" (PD-12) only to members.

import { useEffect, useState } from 'react';
import { suppliersApi } from '../services/apiClient';

const FAILURE_TTL_MS = 60 * 1000;
const CLOSED = Object.freeze({ phase: 0, isSupplierMember: false });

let cached = null; // { value, at, ok }
let inflight = null;

const normalise = (data) => {
  const phase = Number.isInteger(data?.phase) && data.phase > 0 ? data.phase : 0;
  return { phase, isSupplierMember: phase > 0 && data?.isSupplierMember === true };
};

const isFresh = (entry) => !!entry && (entry.ok || Date.now() - entry.at < FAILURE_TTL_MS);

/** Resolve the feature state once; concurrent callers share the request. */
export function fetchSupplierFeature() {
  if (isFresh(cached)) return Promise.resolve(cached.value);
  if (inflight) return inflight;
  inflight = Promise.resolve()
    .then(() => suppliersApi.feature())
    .then(
      (res) => {
        cached = { value: normalise(res?.data), at: Date.now(), ok: true };
        return cached.value;
      },
      () => {
        cached = { value: CLOSED, at: Date.now(), ok: false };
        return CLOSED;
      },
    )
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/** Test seam: forget the cached answer. */
export function __resetSupplierFeatureCache() {
  cached = null;
  inflight = null;
}

/**
 * @returns {{ phase: number, isSupplierMember: boolean, loading: boolean }}
 */
export function useSupplierFeature() {
  const [state, setState] = useState(() =>
    isFresh(cached) ? { ...cached.value, loading: false } : { ...CLOSED, loading: true },
  );

  useEffect(() => {
    if (!state.loading) return undefined;
    let alive = true;
    fetchSupplierFeature().then((value) => {
      if (alive) setState({ ...value, loading: false });
    });
    return () => {
      alive = false;
    };
  }, [state.loading]);

  return state;
}

export default useSupplierFeature;
